/**
 * SettingsDonations — /website-builder/settings/donations
 *
 * Iter 171 — Squarespace-style Donations fund manager.
 *
 *   Left rail (md+): list of funds + "+ New fund"
 *   Right pane:     selected fund editor with 4 collapsing sections
 *                     1. Details        — name + disclaimer
 *                     2. Donation Form  — extra fields collected per donation
 *                     3. Donation Amounts — preset list + custom + cover-fees
 *                     4. Recurring     — weekly / monthly / quarterly / annually
 *
 * The Donations canvas block reads `block.content.fund_id` and merges
 * the fund's amounts/cadence on render — changing values here updates
 * every block using that fund without a re-publish.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Trash2, GripVertical, Loader2, Heart, ChevronDown, ChevronUp,
  Save, Repeat, ArrowRight,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { GlassCard, GradientIconTile, DashboardBackdrop } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const RECURRING_KEYS = [
  { id: 'weekly',    label: 'Weekly' },
  { id: 'monthly',   label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'annually',  label: 'Annually' },
];

function Section({ title, children, defaultOpen = true, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <GlassCard className="overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-white/[0.025]"
      >
        <h3 className="text-white font-semibold font-syne text-[15px]">{title}</h3>
        {open ? <ChevronUp size={16} className="text-white/55" /> : <ChevronDown size={16} className="text-white/55" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 space-y-4">{children}</div>}
    </GlassCard>
  );
}

function AmountRow({ row, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2" data-testid={`amount-row-${row._key}`}>
      <GripVertical size={14} className="text-white/30 shrink-0" />
      <div className="flex items-center bg-white/5 border border-white/10 rounded-lg px-2 w-28 shrink-0">
        <span className="text-white/40 text-xs">$</span>
        <input
          type="number"
          min={1}
          value={row.value || ''}
          onChange={(e) => onChange({ ...row, value: parseInt(e.target.value, 10) || 0 })}
          className="bg-transparent w-full px-1.5 py-1.5 text-white text-sm outline-none"
          data-testid={`amount-value-${row._key}`}
        />
      </div>
      <Input
        value={row.description || ''}
        onChange={(e) => onChange({ ...row, description: e.target.value })}
        placeholder="Add description"
        className="bg-white/5 border-white/10 text-white text-sm rounded-lg flex-1"
        data-testid={`amount-desc-${row._key}`}
      />
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-md text-rose-300/70 hover:text-rose-300 hover:bg-rose-500/10"
        data-testid={`amount-remove-${row._key}`}
        title="Remove"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export default function SettingsDonations() {
  const navigate = useNavigate();
  const { fundId } = useParams();
  const [funds, setFunds] = useState([]);
  const [selectedId, setSelectedId] = useState(fundId || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const fundsById = useMemo(() => Object.fromEntries(funds.map((f) => [f.id, f])), [funds]);
  const current = fundsById[selectedId] || null;

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/donations/funds`, { headers: headers() });
      const list = r.data || [];
      setFunds(list);
      if (list.length) {
        const pick = (fundId && list.find((f) => f.id === fundId)?.id) || list[0].id;
        setSelectedId(pick);
      }
    } catch {
      toast.error('Failed to load donation funds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (current) {
      // Hydrate draft with stable keys on each amount row so React keys are
      // stable across edits even when values change.
      const next = JSON.parse(JSON.stringify(current));
      next.amounts = (next.amounts || []).map((a, i) => ({ ...a, _key: a._key || `a${i}_${Math.random().toString(36).slice(2, 6)}` }));
      setDraft(next);
    } else {
      setDraft(null);
    }
  }, [selectedId, current]);

  const dirty = useMemo(() => {
    if (!draft || !current) return false;
    const a = JSON.stringify({ ...draft, amounts: draft.amounts.map(({ _key, ...r }) => r) });
    const b = JSON.stringify({ ...current, amounts: (current.amounts || []) });
    return a !== b;
  }, [draft, current]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setNested = (path, v) => setDraft((d) => {
    const next = { ...d };
    const keys = path.split('.');
    let ref = next;
    keys.slice(0, -1).forEach((k) => { ref[k] = { ...ref[k] }; ref = ref[k]; });
    ref[keys.at(-1)] = v;
    return next;
  });

  const saveFund = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = { ...draft, amounts: draft.amounts.map(({ _key, ...rest }) => rest) };
      const r = await axios.put(`${API}/builder/donations/funds/${draft.id}`, payload, { headers: headers() });
      setFunds((prev) => prev.map((f) => (f.id === draft.id ? r.data : f)));
      toast.success(`${draft.name} saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const newFund = async () => {
    const name = (await themedPrompt('Name your new fund:', 'Scholarship Fund'));
    if (!name?.trim()) return;
    try {
      const r = await axios.post(`${API}/builder/donations/funds`, { name: name.trim() }, { headers: headers() });
      setFunds((prev) => [...prev, r.data]);
      setSelectedId(r.data.id);
      toast.success('Fund created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  const deleteFund = async () => {
    if (!draft || funds.length <= 1) {
      toast.error('You must keep at least one donation fund.');
      return;
    }
    if (!(await themedConfirm(`Delete "${draft.name}"? This can't be undone.`))) return;
    try {
      await axios.delete(`${API}/builder/donations/funds/${draft.id}`, { headers: headers() });
      const remaining = funds.filter((f) => f.id !== draft.id);
      setFunds(remaining);
      setSelectedId(remaining[0]?.id || null);
      toast.success('Fund deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="relative" data-testid="settings-donations">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-6 pb-12">
        <BackToSettings />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Donations</h1>
            <p className="text-[#A5A8C9] text-sm mt-1.5">Manage funds, donation amounts, recurring cadence, and the form fields collected from donors.</p>
          </div>
          {draft && (
            <button
              onClick={saveFund}
              disabled={!dirty || saving}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="save-fund-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 items-start">
          {/* Funds rail */}
          <GlassCard className="p-3" data-testid="funds-rail">
            <div className="flex items-center justify-between mb-2 px-2 pt-1">
              <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Funds</p>
              <button
                type="button"
                onClick={newFund}
                className="p-1.5 rounded-md text-white/65 hover:text-white hover:bg-white/10"
                title="Add fund"
                data-testid="new-fund-btn"
              >
                <Plus size={14} />
              </button>
            </div>
            {loading ? (
              <div className="flex items-center gap-2 text-white/55 px-2 py-3"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            ) : (
              <div className="space-y-1">
                {funds.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => { setSelectedId(f.id); navigate(`/website-builder/settings/donations/${f.id}`); }}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all flex items-center gap-2 ${selectedId === f.id ? 'border-violet-400/50 bg-violet-500/[0.08]' : 'border-transparent bg-white/[0.02] hover:border-white/10'}`}
                    data-testid={`fund-pick-${f.id}`}
                  >
                    <GradientIconTile icon={Heart} ramp="fuchsia" size="xs" />
                    <span className="text-white text-[13px] font-medium truncate flex-1">{f.name}</span>
                  </button>
                ))}
              </div>
            )}
          </GlassCard>

          {/* Right pane */}
          <div className="space-y-4 min-w-0">
            {draft ? (
              <>
                <Section title="Details" testid="section-details">
                  <div>
                    <Label className="text-[#A5A8C9] text-[11px]">Name</Label>
                    <Input value={draft.name || ''} onChange={(e) => setField('name', e.target.value)} placeholder="General Fund" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="fund-name" />
                  </div>
                  <div>
                    <Label className="text-[#A5A8C9] text-[11px]">Disclaimer (optional)</Label>
                    <Textarea value={draft.disclaimer || ''} maxLength={560} onChange={(e) => setField('disclaimer', e.target.value)} placeholder="Add any tax status information or terms…" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl min-h-[80px]" data-testid="fund-disclaimer" />
                    <p className="mt-1 text-[10.5px] text-white/40">{(draft.disclaimer || '').length}/560 — shown beneath donation amounts.</p>
                  </div>
                </Section>

                <Section title="Donation Amounts" testid="section-amounts">
                  <p className="text-[11px] text-white/45 -mt-2">Preset amounts donors can pick. The first row is the default selection.</p>
                  <div className="space-y-2">
                    {(draft.amounts || []).map((row, i) => (
                      <AmountRow
                        key={row._key}
                        row={row}
                        onChange={(next) => {
                          const copy = [...draft.amounts];
                          copy[i] = next;
                          setField('amounts', copy);
                        }}
                        onRemove={() => {
                          if (draft.amounts.length <= 1) { toast.error('Keep at least one amount.'); return; }
                          setField('amounts', draft.amounts.filter((_, j) => j !== i));
                        }}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setField('amounts', [...(draft.amounts || []), { value: 0, description: '', _key: `a${draft.amounts.length}_${Math.random().toString(36).slice(2, 6)}` }])}
                    className="text-[11px] uppercase tracking-wider font-bold px-3 py-2 rounded-lg bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white flex items-center gap-1.5"
                    data-testid="add-amount-btn"
                  >
                    <Plus size={11} /> Add amount
                  </button>

                  <div className="pt-3 border-t border-white/8 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-white text-sm font-medium">Custom amounts</p>
                      <p className="text-[11px] text-white/50">Donors can enter any donation amount.</p>
                    </div>
                    <Switch checked={!!draft.custom_amounts_enabled} onCheckedChange={(v) => setField('custom_amounts_enabled', v)} data-testid="toggle-custom-amounts" />
                  </div>

                  <div className="pt-3 border-t border-white/8">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-white text-sm font-medium">Cover the fees</p>
                        <p className="text-[11px] text-white/50">Give donors the option to add an additional amount to donations.</p>
                      </div>
                      <Switch checked={!!draft.cover_fees_enabled} onCheckedChange={(v) => setField('cover_fees_enabled', v)} data-testid="toggle-cover-fees" />
                    </div>
                    {draft.cover_fees_enabled && (
                      <div className="mt-3 grid grid-cols-[110px_1fr] gap-3 items-start">
                        <div>
                          <Label className="text-[#A5A8C9] text-[11px]">Percentage (%)</Label>
                          <Input type="number" min={0} max={100} step={0.1} value={draft.cover_fees_percent ?? 3} onChange={(e) => setField('cover_fees_percent', parseFloat(e.target.value) || 0)} className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="cover-fees-percent" />
                        </div>
                        <div>
                          <Label className="text-[#A5A8C9] text-[11px]">Description</Label>
                          <Input value={draft.cover_fees_description || ''} onChange={(e) => setField('cover_fees_description', e.target.value)} placeholder="Support us by covering the fees we have to pay" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="cover-fees-description" />
                        </div>
                      </div>
                    )}
                  </div>
                </Section>

                <Section title="Recurring Donations" testid="section-recurring">
                  <p className="text-[11px] text-white/45 -mt-2">Toggle the cadences donors can pick. The default frequency is auto-selected in the dropdown when this fund is active.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {RECURRING_KEYS.map((rk) => (
                      <div key={rk.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5">
                        <span className="text-white text-[12.5px] flex items-center gap-1.5"><Repeat size={11} className="text-white/55" /> {rk.label}</span>
                        <Switch
                          checked={!!draft.recurring?.[rk.id]}
                          onCheckedChange={(v) => setNested(`recurring.${rk.id}`, v)}
                          data-testid={`recurring-${rk.id}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label className="text-[#A5A8C9] text-[11px]">Default frequency</Label>
                    <select
                      value={draft.recurring?.default_frequency || 'monthly'}
                      onChange={(e) => setNested('recurring.default_frequency', e.target.value)}
                      data-testid="recurring-default"
                      className="w-full mt-1.5 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400"
                    >
                      <option value="one-time" className="bg-[#15152E]">One-time</option>
                      {RECURRING_KEYS.filter((rk) => draft.recurring?.[rk.id]).map((rk) => (
                        <option key={rk.id} value={rk.id} className="bg-[#15152E]">{rk.label}</option>
                      ))}
                    </select>
                  </div>
                </Section>

                {/* Delete affordance */}
                {funds.length > 1 && (
                  <button
                    type="button"
                    onClick={deleteFund}
                    className="text-rose-300/70 hover:text-rose-300 text-[12px] font-medium inline-flex items-center gap-1.5"
                    data-testid="delete-fund-btn"
                  >
                    <Trash2 size={12} /> Delete this fund
                  </button>
                )}
              </>
            ) : (
              <GlassCard className="p-10 text-center" data-testid="no-fund-card">
                <p className="text-white/55 text-sm">{loading ? 'Loading…' : 'No fund selected.'}</p>
                <button onClick={newFund} className="mt-4 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"><Plus size={12} /> Create a fund</button>
              </GlassCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
